import config from '../config';
import { getBffHeaders } from '../utils/apiHeaders.js';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';

const base = (projectId) => `${config.bffUrl}/api/mia-platform/projects/${encodeURIComponent(projectId)}/publish`;

// The server's release gate (vfs-service, through the BFF): 409 `{ code: 'quality_gate_blocked', blocking }` while
// Quality blocks Publish. The error carries `code` and `blocking` so the dialog shows its gate banner.
export const GATE_BLOCKED = 'quality_gate_blocked';

async function readJson(response, fallback) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.success === false) {
    const error = new Error(body.error || fallback);
    error.details = body.details;
    error.repoUrl = body.repoUrl;
    if (body.code) { error.code = body.code; }
    if (Array.isArray(body.blocking)) { error.blocking = body.blocking; }
    error.status = response.status;
    throw error;
  }
  return body;
}

// What would be published, what is missing, and which destinations the user can use.
export async function checkPublish(projectId) {
  const response = await fetch(`${base(projectId)}/check`, { headers: getBffHeaders({}, true), credentials: 'include' });
  return readJson(response, 'The project could not be checked.');
}

// Git, GitOps and the image registry go straight to vfs-service: PublishTargetsService.js (the BFF's /publish/gitlab
// had no account to push with).

// The release vfs-service remembers for a served zip (Quality's build provenance): `X-Flow-Release-Id`. Null when the
// header is missing or not readable (a cross-origin answer that does not expose it): the server then falls back to the
// project's latest pending release.
export function releaseIdOf(response) {
  try {
    return response?.headers?.get?.('X-Flow-Release-Id') || null;
  } catch {
    return null;
  }
}

// The publishable files as a zip Blob (no secrets, dependencies or build output; chat history and brain excluded).
// `onRelease(releaseId)` is called when the answer names its release.
export async function exportZip(projectId, name, { onRelease } = {}) {
  const response = await fetch(`${base(projectId)}/export.zip?name=${encodeURIComponent(name)}`, {
    headers: getBffHeaders({}, true),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('The zip could not be created.');
  }
  const releaseId = releaseIdOf(response);
  if (releaseId && onRelease) { onRelease(releaseId); }
  return response.blob();
}

// The same zip for a release (the Console delivery): refused with 409 `quality_gate_blocked` while the gate blocks.
// `onRelease(releaseId)` is called when the answer names its release (`X-Flow-Release-Id`).
export async function releaseZip(projectId, name, { onRelease } = {}) {
  const response = await fetch(`${base(projectId)}/release.zip?name=${encodeURIComponent(name)}`, {
    headers: getBffHeaders({}, true),
    credentials: 'include'
  });
  if (!response.ok) {
    await readJson(response, 'The project could not be packed for the release.');
  }
  const releaseId = releaseIdOf(response);
  if (releaseId && onRelease) { onRelease(releaseId); }
  return response.blob();
}

// Downloads the publishable files as a zip (the browser needs the auth headers, so this is a fetch, not a link).
// Resolves to `{ releaseId }` (null when the answer did not name its release).
export async function downloadZip(projectId, name) {
  let releaseId = null;
  const url = URL.createObjectURL(await exportZip(projectId, name, { onRelease: (id) => { releaseId = id; } }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name}.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return { releaseId };
}

/** How often the Lockfiles step's job is asked for, and for how long at most (20 services, minutes each). */
export const LOCKFILES_POLL_MS = 2000;
export const LOCKFILES_MAX_WAIT_MS = 75 * 60 * 1000;

const lockfilesFailure = (message) => new Error(`The lockfiles could not be checked: ${message}.`);
const pause = (ms, signal) => new Promise((resolve, reject) => {
  const timer = setTimeout(resolve, ms);
  signal?.addEventListener('abort', () => { clearTimeout(timer); reject(signal.reason || new Error('cancelled')); }, { once: true });
});

/**
 * The Lockfiles step of a Console delivery (vfs-service `POST /publish/console/lockfiles`, src/publish/deliveryLockfiles.js):
 * the lockfiles of the services that go are checked against their manifests and regenerated in the sandbox where missing
 * or out of sync (a service that has not run since the sandbox woke too: in a copy of its code), then committed
 * ("Update lockfiles for delivery"). Never starts a service. It can take minutes, so vfs-service answers 202 with a job
 * (src/publish/deliveryLockfilesJob.js) that this polls (`GET .../lockfiles/:jobId`) until it is done; `onProgress(job)`
 * on each answer (`{ total, done, current }`). A sandbox from before the job answers the report at once (200). Resolves
 * with `{ state: 'none'|'updated'|'failed', updated, checked, commits, services: [{ service, status, file?, message,
 * output?, fix?, blocking? }], message }`; a sandbox older than the step answers 404: `{ state: 'unavailable', services: [] }`.
 */
export async function prepareDeliveryLockfiles(projectId, services, { onProgress, signal, pollMs = LOCKFILES_POLL_MS, maxWaitMs = LOCKFILES_MAX_WAIT_MS } = {}) {
  const vfs = (config.vfsServiceUrl || '').replace(/\/$/, '');
  if (!vfs) { return { state: 'unavailable', services: [] }; }
  const base = `${vfs}/api/v1/projects/${encodeURIComponent(projectId)}/publish/console/lockfiles`;
  const headers = { Accept: 'application/json', 'Content-Type': 'application/json', ...buildAclContextHeaders() };
  let response;
  try {
    response = await getAuthorizedFetch()(base, { method: 'POST', headers, body: JSON.stringify(services && services.length ? { services } : {}), signal });
  } catch (error) {
    throw lockfilesFailure(error.message || 'the sandbox did not answer');
  }
  if (response.status === 404) { return { state: 'unavailable', services: [] }; }
  const started = await readJson(response, 'The lockfiles could not be checked.');
  if (!started.job) { return started; }
  const { id } = started.job;
  const deadline = Date.now() + maxWaitMs;
  let job = started.job;
  for (;;) {
    if (job) {
      if (onProgress) { onProgress(job); }
      if (job.state === 'done') { return job.result; }
      if (job.state === 'failed') { throw lockfilesFailure(job.error || 'the sandbox stopped the check'); }
    }
    if (Date.now() > deadline) { throw lockfilesFailure('it took too long'); }
    await pause(pollMs, signal);
    job = null;
    try {
      response = await getAuthorizedFetch()(`${base}/${encodeURIComponent(id)}`, { headers: { Accept: 'application/json', ...buildAclContextHeaders() }, signal });
    } catch (error) {
      // A poll that does not get through (a network blip) is asked again; the job goes on in the sandbox.
      if (signal?.aborted) { throw error; }
      continue;
    }
    ({ job = null } = await readJson(response, 'The lockfiles could not be checked.'));
  }
}
